import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NotificationService } from '../../../services/notification.service'; // NotificationService;
import { Subject, of } from 'rxjs';
import { catchError, finalize, takeUntil } from 'rxjs/operators';
import {
  RewashSettingsRow,
  RewashSettingsService,
} from '../../../services/rewash-settings.service';

type RewashWindowMode = 'never' | 'custom' | null;

@Component({
  selector: 'app-rewash-settings',
  standalone: true,
  imports: [CommonModule, FormsModule],
  providers: [NotificationService],
  templateUrl: './rewash-settings.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./rewash-settings.component.scss'],
})
export class RewashSettingsComponent implements OnChanges, OnDestroy {
  private readonly rewashSettingsService = inject(RewashSettingsService);
  private readonly messageService = inject(NotificationService);
  private readonly destroy$ = new Subject<void>();

  @Input() isActive = false;
  /**
   * Mirrors `isLoading` so a host can hold its own reveal until this section has loaded,
   * instead of painting itself first and leaving this one to pop in after (CWS-598).
   */
  @Output() loadingChange = new EventEmitter<boolean>();
  @ViewChild('customPanel') customPanel?: ElementRef<HTMLElement>;
  @ViewChild('customMinutesInput') customMinutesInput?: ElementRef<HTMLInputElement>;

  selectedMode: RewashWindowMode = null;
  customMinutesDraft = '';
  customError = '';

  /** `undefined` until first successful load for this open cycle. */
  savedWindowMinutes: number | undefined = undefined;
  isConfigured = false;

  minMinutes = 1;
  maxMinutes = 1440;

  isLoading = false;
  isSavingNever = false;
  isSavingCustom = false;

  get isInteractionDisabled(): boolean {
    return this.isLoading || this.isSavingNever || this.isSavingCustom;
  }

  get showCustomPanel(): boolean {
    return this.selectedMode === 'custom';
  }

  get isCustomSaveDisabled(): boolean {
    if (this.isInteractionDisabled || this.selectedMode !== 'custom') {
      return true;
    }
    const parsed = this.parseCustomMinutes();
    return parsed !== null && this.isConfigured && this.savedWindowMinutes === parsed;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isActive']?.currentValue === true) {
      this.loadSettings();
    }
  }

  private setLoading(loading: boolean): void {
    if (this.isLoading === loading) {
      return;
    }
    this.isLoading = loading;
    this.loadingChange.emit(loading);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onNeverSelected(): void {
    if (this.isInteractionDisabled) {
      return;
    }
    if (this.selectedMode === 'never' && !this.isConfigured) {
      return;
    }

    this.selectedMode = 'never';
    this.customError = '';
    this.persistNever();
  }

  onCustomSelected(): void {
    if (this.isInteractionDisabled) {
      return;
    }

    this.selectedMode = 'custom';
    this.customError = '';

    if (this.isConfigured && typeof this.savedWindowMinutes === 'number') {
      this.customMinutesDraft = String(this.savedWindowMinutes);
    }

    this.scrollCustomPanelIntoView();
  }

  onCustomMinutesDraftChange(): void {
    this.customError = '';
  }

  onCustomSave(): void {
    if (this.isCustomSaveDisabled) {
      return;
    }

    const parsed = this.parseCustomMinutes();
    if (parsed === null) {
      this.customError = `Enter a whole number between ${this.minMinutes} and ${this.maxMinutes}.`;
      return;
    }

    this.customError = '';
    this.isSavingCustom = true;
    this.rewashSettingsService
      .updateRewashSettings({ window_minutes: parsed })
      .pipe(
        takeUntil(this.destroy$),
        catchError(err => {
          this.messageService.add({
            severity: 'error',
            summary: 'Save failed',
            detail: err.error?.message || 'Could not save rewash settings.',
          });
          return of(null);
        }),
        finalize(() => {
          this.isSavingCustom = false;
        })
      )
      .subscribe(res => {
        if (res == null) {
          return;
        }
        if (res.status === false) {
          this.messageService.add({
            severity: 'error',
            summary: 'Save failed',
            detail: res.message || 'The server did not save rewash settings.',
          });
          return;
        }

        this.applySavedRow(res.data);
        this.messageService.add({
          severity: 'success',
          summary: 'Saved',
          detail: res.message || 'Rewash settings saved successfully.',
        });
      });
  }

  private loadSettings(): void {
    this.setLoading(true);
    this.customError = '';
    this.rewashSettingsService
      .getRewashSettings()
      .pipe(
        takeUntil(this.destroy$),
        catchError(err => {
          this.messageService.add({
            severity: 'error',
            summary: 'Load failed',
            detail: err.error?.message || 'Could not load rewash settings.',
          });
          return of(null);
        }),
        finalize(() => {
          this.setLoading(false);
        })
      )
      .subscribe(res => {
        if (res == null) {
          return;
        }
        if (res.status === false) {
          this.messageService.add({
            severity: 'error',
            summary: 'Load failed',
            detail: res.message || 'Could not load rewash settings.',
          });
          return;
        }

        if (typeof res.window_minutes_min === 'number') {
          this.minMinutes = res.window_minutes_min;
        }
        if (typeof res.window_minutes_max === 'number') {
          this.maxMinutes = res.window_minutes_max;
        }

        if (res.data == null) {
          this.isConfigured = false;
          this.savedWindowMinutes = undefined;
          this.selectedMode = 'never';
          this.customMinutesDraft = '';
          return;
        }

        this.applySavedRow(res.data);
      });
  }

  private persistNever(): void {
    const previousMode = this.resolveModeFromSaved();
    this.isSavingNever = true;

    this.rewashSettingsService
      .deleteRewashSettings()
      .pipe(
        takeUntil(this.destroy$),
        catchError(err => {
          this.selectedMode = previousMode;
          this.messageService.add({
            severity: 'error',
            summary: 'Save failed',
            detail: err.error?.message || 'Could not disable rewash.',
          });
          return of(null);
        }),
        finalize(() => {
          this.isSavingNever = false;
        })
      )
      .subscribe(res => {
        if (res == null) {
          return;
        }
        if (res.status === false) {
          this.selectedMode = previousMode;
          this.messageService.add({
            severity: 'error',
            summary: 'Save failed',
            detail: res.message || 'Could not disable rewash.',
          });
          return;
        }

        this.applySavedRow(res.data);
        this.messageService.add({
          severity: 'success',
          summary: 'Saved',
          detail: res.message || 'Rewash disabled successfully.',
        });
      });
  }

  private applySavedRow(row: RewashSettingsRow | null | undefined): void {
    if (row == null) {
      this.isConfigured = false;
      this.savedWindowMinutes = undefined;
      this.selectedMode = 'never';
      this.customMinutesDraft = '';
      return;
    }

    this.isConfigured = true;
    this.savedWindowMinutes = Number(row.window_minutes);
    this.selectedMode = 'custom';
    this.customMinutesDraft = String(this.savedWindowMinutes);
  }

  private resolveModeFromSaved(): RewashWindowMode {
    if (!this.isConfigured) {
      return 'never';
    }
    return 'custom';
  }

  private scrollCustomPanelIntoView(): void {
    setTimeout(() => {
      this.customPanel?.nativeElement.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
      this.customMinutesInput?.nativeElement.focus({ preventScroll: true });
    });
  }

  private parseCustomMinutes(): number | null {
    const raw = String(this.customMinutesDraft ?? '').trim();
    if (!raw) {
      return null;
    }
    const parsed = Number(raw);
    if (!Number.isInteger(parsed)) {
      return null;
    }
    if (parsed < this.minMinutes || parsed > this.maxMinutes) {
      return null;
    }
    return parsed;
  }
}
